/**
 * 관리자 Route
 * --
 */
import { useState, useEffect } from 'react';
import { AdminLayout, ApplicationStatus, PrivateRoute } from 'components';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  BarcodeOutlined,
  DollarOutlined,
  ProjectOutlined,
  SolutionOutlined,
  StarOutlined,
  TruckOutlined,
  IdcardOutlined,
  WalletOutlined,
  ShoppingCartOutlined,
  FundProjectionScreenOutlined,
  MailOutlined,
  HomeOutlined,
  HddOutlined,
} from '@ant-design/icons';
import { useUserAction, useUserStack } from 'store';
import { useAuthCheck } from 'hooks/AuthHooks';
import { useGetShopInformation } from 'hooks/ShopInformationHooks';
import { deleteCookie, DEPLOY_TYPE, DOMAIN_URL, getCookie } from 'utils';
import { Button } from 'antd';

// Route
import AdminHome from './AdminHome';

import {
  UserDashboard,
  UserDetail,
  UserList,
  UserPeriodList,
  UserRegister,
} from './admin.users';
import {
  ManagerRegister,
  ManagerList,
} from 'routes/admin.routes/admin.managers';
import { RatingDetail, RatingList, RatingRegister } from './shop.rating';
import {
  ProductAuditList,
  ProductDetail,
  ProductList,
  ProductOrderList,
  // ProductRegister,
} from './shop.products';
import { ProductRegister } from 'routes/admin.routes/admin.products';
import {
  PromotionDetail,
  PromotionList,
  PromotionRegister,
  ProposalDetail,
  ProposalList,
} from './shop.promotion';
import { ChargeList } from './shop.charge';
import {
  CalculateApplication,
  CalculateDetail,
  CalculateList,
} from './shop.calculate';
import { NoticeDetail, NoticeList } from './shop.notice';
import { PopupDetail, PopupList } from './shop.popup';
import { BannerDetail, BannerList } from './shop.banner';
import { PolicyDetail, PolicyList } from './shop.policy';
import { SEOList, SEOMain } from './shop.seo';
import { ReviewDetail, ReviewList } from './shop.review';
import {
  BrandAuditDetail,
  BrandAuditList,
  BrandDashboard,
  BrandDetail,
  BrandList,
} from './shop.brand';
import { StoreDetail, StoreList } from './shop.stores';
import { CategoryList } from './shop.categories';
import { MenuDetail, MenuList } from './shop.menus';
import { TagDetail, TagList, TagRegister } from './shop.tags';
import {
  SearchWordDetail,
  SearchWordList,
  SearchWordRegister,
} from './shop.searchword';
// import { ManagerList, ManagerRegister } from './shop.managers';
import { InquiryList } from './shop.inquiry';
import {
  MessageTemplate,
  EmailList,
  SMSList,
  NotificationList,
} from './shop.message';
import {
  StatisticDashboard,
  StatisticDetail,
  StatisticList,
} from './shop.statistics';
import { CartAnalyze, CartList, CartSettings } from './shop.cart';
import { SaleDashboard, SaleDetail, SaleList } from './shop.sale';
import {
  OrderList,
  ShippingList,
  RegularList,
  RentList,
  PaymentList,
  RefundList,
} from './shop.orders';
import {
  GiftCategoryList,
  GiftDashboard,
  GiftDetail,
  GiftList,
  GiftProductDetail,
  GiftProductList,
} from './shop.gift';
import Auth from './shop.auth';
import Apply from './shop.apply';
import {
  Main,
  ProductDashboard,
  ReviewDashboard,
  OrderDashboard,
} from './shop.dashboard';
import { ShopInformation } from './shop.information';
import ForwardingLocationContainer from 'routes/admin.routes/admin.forwardingLocation';

const AdminRoutes = () => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  /* ===== Actions ===== */
  // 유저 Store
  const userStack = useUserStack();
  // 유저 Store Action
  const userAction = useUserAction();

  /* ===== Mutate ===== */
  // 토큰 검증
  const { authCheck } = useAuthCheck();
  // 쇼핑몰 단일 조회
  const { data: shopInformation, refetch: shopInformationRefetch } =
    useGetShopInformation(userStack?.shop_information_id);

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 사이드메뉴 선택 키
  const [selectedKeys, setSelectedKeys] = useState(['users']);
  const [parentPath, setParentPath] = useState('');
  const [currentPath, setCurrentPath] = useState(pathname);

  /* ===== Variable ===== */
  // 사이드 메뉴
  const menu = [
    {
      key: '/main',
      icon: <HomeOutlined />,
      label: '홈',
      // children: [],
      onClick: () => navigate(`/main`),
    },
    // {
    //   key: 'user-management',
    //   icon: <UserOutlined />,
    //   label: '회원 관리',
    //   children: [
    //     {
    //       key: 'users-dashboard',
    //       label: '대시보드',
    //       icon: null,
    //       onClick: () => navigate(`/users/dashboard`),
    //     },
    //     {
    //       key: 'users-list',
    //       label: '회원 목록',
    //       icon: null,
    //       onClick: () => navigate(`/users/list`),
    //     },
    //     {
    //       key: 'users-period-list',
    //       label: '기간별 회원 목록',
    //       icon: null,
    //       onClick: () => navigate(`/users/period`),
    //     },
    //     {
    //       key: 'users-register',
    //       label: '회원 등록/수정',
    //       icon: null,
    //       onClick: () => navigate(`/users/register`),
    //     },
    //     {
    //       key: 'users-rating',
    //       label: '회원등급 관리',
    //       icon: null,
    //       onClick: () => navigate(`/users/rating`),
    //     },
    //   ],
    //   type: null,
    // },
    {
      key: '/product/management',
      icon: <BarcodeOutlined />,
      label: '상품/재고 관리',
      children: [
        {
          key: '/product/dashboard',
          label: '상품 대시보드',
          icon: null,
          onClick: () => navigate(`/product/dashboard`),
        },
        {
          key: '/product/list',
          label: '상품 목록',
          icon: null,
          onClick: () => navigate(`/product/list`),
        },
        {
          key: '/product/register',
          label: '상품 등록',
          icon: null,
          onClick: () => navigate(`/product/register`),
        },
        // {
        //   key: '/product-order/list',
        //   label: '발주 목록',
        //   icon: null,
        //   onClick: () => navigate(`/product-order/list`),
        // },
        {
          key: '/product-audit/list',
          label: '상품심사 목록',
          icon: null,
          onClick: () => navigate(`/product-audit/list`),
        },
        {
          key: '/forwarding/location',
          label: '출고지 관리',
          icon: null,
          onClick: () => navigate(`/forwarding/location`),
        },
      ],
      type: null,
    },
    // {
    //   key: '/promotion/management',
    //   icon: <ProjectOutlined />,
    //   label: '프로모션 관리',
    //   children: [
    //     {
    //       key: '/promotion/list',
    //       label: '프로모션 목록',
    //       icon: null,
    //       onClick: () => navigate(`/promotion/list`),
    //     },
    //     {
    //       key: '/proposal/list',
    //       label: '프로모션 제안 목록',
    //       icon: null,
    //       onClick: () => navigate(`/proposal/list`),
    //     },
    //   ],
    //   type: null,
    // },
    {
      key: '/calculate/management',
      icon: <DollarOutlined />,
      label: '정산 관리',
      children: [
        {
          key: '/calculate/list',
          label: '정산내역 조회',
          icon: null,
          onClick: () => navigate(`/calculate/list`),
        },
        {
          key: '/calculate/application',
          label: '출금신청 목록',
          icon: null,
          onClick: () => navigate(`/calculate/application`),
        },
        // {
        //   key: '/calculate/charge',
        //   label: '수수료 관리',
        //   icon: null,
        //   onClick: () => navigate(`/calculate/charge`),
        // },
      ],
      type: null,
    },
    // {
    //   key: '/site/management',
    //   icon: <SettingOutlined />,
    //   label: '사이트 관리',
    //   children: [
    //     {
    //       key: '/notice/list',
    //       label: '공지사항 관리',
    //       icon: null,
    //       onClick: () => navigate(`/notice/list`),
    //     },
    //     {
    //       key: '/popup/list',
    //       label: '팝업 관리',
    //       icon: null,
    //       onClick: () => navigate(`/popup/list`),
    //     },
    //     {
    //       key: '/banner/list',
    //       label: '배너 관리',
    //       icon: null,
    //       onClick: () => navigate(`/banner/list`),
    //     },
    //     {
    //       key: '/policy/list',
    //       label: '정책 관리',
    //       icon: null,
    //       onClick: () => navigate(`/policy/list`),
    //     },
    //     {
    //       key: '/seo/list',
    //       label: '검색엔진 최적화 관리(SEO)',
    //       icon: null,
    //       onClick: () => navigate(`/seo/list`),
    //     },
    //   ],
    //   type: null,
    // },
    {
      key: '/review/management',
      icon: <StarOutlined />,
      label: '리뷰 관리',
      children: [
        {
          key: '/review/dashboard',
          label: '리뷰 대시보드',
          icon: null,
          onClick: () => navigate(`/review/dashboard`),
        },
        {
          key: '/review/list',
          label: '리뷰 목록',
          icon: null,
          onClick: () => navigate(`/review/list`),
        },
      ],
      type: null,
    },
    /* {
      key: '/brand/management',
      icon: <SolutionOutlined />,
      label: '입점업체 관리',
      children: [
        // {
        //   key: '/brand/dashboard',
        //   label: '대시보드',
        //   icon: null,
        //   onClick: () => navigate(`/brand/dashboard`),
        // },
        // {
        //   key: '/brand/list',
        //   label: '입점 목록',
        //   icon: null,
        //   onClick: () => navigate(`/brand/list`),
        // },
        {
          key: '/brand-audit/list',
          label: '입점심사 목록',
          icon: null,
          onClick: () => navigate(`/brand-audit/list`),
        },
      ],
      type: null,
    }, */
    // {
    //   key: '/store/management',
    //   icon: <ShopOutlined />,
    //   label: '매장 관리',
    //   children: [
    //     {
    //       key: '/store/list',
    //       label: '매장 관리',
    //       icon: null,
    //       onClick: () => navigate(`/store/list`),
    //     },
    //   ],
    //   type: null,
    // },
    // {
    //   key: 'category-management',
    //   icon: <BarsOutlined />,
    //   label: '카테고리 관리',
    //   children: [
    //     {
    //       key: 'categories',
    //       label: '카테고리 관리',
    //       icon: null,
    //       onClick: () => navigate(`/categories/list`),
    //     },
    //   ],
    //   type: null,
    // },
    // {
    //   key: 'gift-management',
    //   icon: <GiftOutlined />,
    //   label: '선물 관리',
    //   children: [
    //     {
    //       key: 'gift-dashboard',
    //       label: '대시보드',
    //       icon: null,
    //       onClick: () => navigate(`/gift/dashboard`),
    //     },
    //     {
    //       key: 'gift-list',
    //       label: '선물 내역 관리',
    //       icon: null,
    //       onClick: () => navigate(`/gift/list`),
    //     },
    //     {
    //       key: 'gift-product',
    //       label: '선물 상품 관리',
    //       icon: null,
    //       onClick: () => navigate(`/gift/products`),
    //     },
    //     {
    //       key: 'gift-category',
    //       label: '선물 카테고리 관리',
    //       icon: null,
    //       onClick: () => navigate(`/gift/categories`),
    //     },
    //   ],
    //   type: null,
    // },
    // {
    //   key: 'menu-management',
    //   icon: <CoffeeOutlined />,
    //   label: '메뉴 관리',
    //   children: [
    //     {
    //       key: 'menu-list',
    //       label: '메뉴 목록',
    //       icon: null,
    //       onClick: () => navigate(`/menu/list`),
    //     },
    //   ],
    //   type: null,
    // },
    // {
    //   key: 'tag-management',
    //   icon: <TagsOutlined />,
    //   label: '태그 관리',
    //   children: [
    //     {
    //       key: 'tag-list',
    //       label: '태그 목록',
    //       icon: null,
    //       onClick: () => navigate(`/tag/list`),
    //     },
    //     {
    //       key: 'tag-detail',
    //       label: '태그 상세정보',
    //       icon: null,
    //       onClick: () => navigate(`/tag/detail`),
    //     },
    //     {
    //       key: 'tag-register',
    //       label: '태그 등록/수정',
    //       icon: null,
    //       onClick: () => navigate(`/tag/register`),
    //     },
    //   ],
    //   type: null,
    // },
    // {
    //   key: 'searchword-management',
    //   icon: <SearchOutlined />,
    //   label: '추천검색어 관리',
    //   children: [
    //     {
    //       key: 'searchword-list',
    //       label: '추천검색어 목록',
    //       icon: null,
    //       onClick: () => navigate(`/searchword/list`),
    //     },
    //     {
    //       key: 'searchword-detail',
    //       label: '추천검색어 상세정보',
    //       icon: null,
    //       onClick: () => navigate(`/searchword/detail`),
    //     },
    //     {
    //       key: 'searchword-register',
    //       label: '추천검색어 등록/수정',
    //       icon: null,
    //       onClick: () => navigate(`/searchword/register`),
    //     },
    //   ],
    //   type: null,
    // },
    {
      key: '/order/management',
      icon: <TruckOutlined />,
      label: '주문 관리',
      children: [
        {
          key: '/order/dashboard',
          label: '주문관리 대시보드',
          icon: null,
          onClick: () => navigate(`/order/dashboard`),
        },
        {
          key: '/order/list/all',
          label: '전체주문조회',
          icon: null,
          onClick: () => navigate(`/order/list`),
        },
        {
          key: '/order/list/PENDING',
          label: '입금전 관리',
          icon: null,
          onClick: () => navigate(`/order/list/PENDING`),
        },
        {
          key: '/shipping/list/PENDING',
          label: '상품 준비중',
          icon: null,
          onClick: () => navigate(`/shipping/list/PENDING`),
        },
        {
          key: '/shipping/list/PREPARING',
          label: '배송 준비중',
          icon: null,
          onClick: () => navigate(`/shipping/list/PREPARING`),
        },
        {
          key: '/shipping/list/ON_HOLD',
          label: '배송 보류',
          icon: null,
          onClick: () => navigate(`/shipping/list/ON_HOLD`),
        },
        {
          key: '/shipping/list/SHIPPING',
          label: '배송 중',
          icon: null,
          onClick: () => navigate(`/shipping/list/SHIPPING`),
        },
        {
          key: '/shipping/list/DELIVERED',
          label: '배송 완료',
          icon: null,
          onClick: () => navigate(`/shipping/list/DELIVERED`),
        },
        {
          key: '/shipping/list/EXCHANGE_REQUESTED',
          label: '교환 신청',
          icon: null,
          onClick: () => navigate(`/shipping/list/EXCHANGE_REQUESTED`),
        },
        {
          key: '/shipping/list/RETURN_REQUESTED',
          label: '반품 신청',
          icon: null,
          onClick: () => navigate(`/shipping/list/RETURN_REQUESTED`),
        },
        {
          key: '/refund/list/RETURN',
          label: '교환/반품/환불 처리완료',
          icon: null,
          onClick: () => navigate(`/refund/list`),
        },
      ],
      type: null,
    },
    {
      key: '/admin/management',
      icon: <IdcardOutlined />,
      label: '관리자 관리',
      children: [
        {
          key: `/manager/list`,
          label: '관리자 목록',
          icon: null,
          onClick: () => navigate(`/manager/list`),
        },
        {
          key: `/manager/register`,
          label: '관리자 등록',
          icon: null,
          onClick: () => navigate(`/manager/register`),
        },
      ],
      type: null,
    },
    // {
    //   key: '/sales/management',
    //   icon: <WalletOutlined />,
    //   label: '매출 관리',
    //   children: [
    //     // {
    //     //   key: '/sales/dashboard',
    //     //   label: '대시보드',
    //     //   icon: null,
    //     //   onClick: () => navigate(`/sales/dashboard`),
    //     // },
    //     {
    //       key: '/sales/dashboard/daily',
    //       label: '일자별',
    //       icon: null,
    //       onClick: () => navigate(`/sales/dashboard/daily`),
    //     },
    //     {
    //       key: '/sales/dashboard/menu',
    //       label: '메뉴별',
    //       icon: null,
    //       onClick: () => navigate(`/sales/dashboard/menu`),
    //     },
    //     {
    //       key: '/sales/dashboard/payments',
    //       label: '결제수단별',
    //       icon: null,
    //       onClick: () => navigate(`/sales/dashboard/payments`),
    //     },
    //     // {
    //     //   key: 'sales-list',
    //     //   label: '매출 목록',
    //     //   icon: null,
    //     //   onClick: () => navigate(`/sales/list`),
    //     // },
    //     // {
    //     //   key: 'store-sales-list',
    //     //   label: '매장매출 목록',
    //     //   icon: null,
    //     //   onClick: () => navigate(`/store-sales/list`),
    //     // },
    //   ],
    //   type: null,
    // },
    {
      key: '/cart/management',
      icon: <ShoppingCartOutlined />,
      label: '장바구니 관리',
      children: [
        {
          key: '/cart/analyze',
          label: '장바구니 분석',
          icon: null,
          onClick: () => navigate(`/cart/analyze`),
        },
        // {
        //   key: 'cart-setting',
        //   label: '장바구니 설정',
        //   icon: null,
        //   onClick: () => navigate(`/cart/settings`),
        // },
      ],
      type: null,
    },
    // {
    //   key: '/statistics/management',
    //   icon: <FundProjectionScreenOutlined />,
    //   label: '통계 관리',
    //   children: [
    //     {
    //       key: '/statistics/dashboard',
    //       label: '대시보드',
    //       icon: null,
    //       onClick: () => navigate(`/statistics/dashboard`),
    //     },
    //   ],
    //   type: null,
    // },
    {
      key: '/inquiry/management',
      icon: <MailOutlined />,
      label: '문의 관리',
      children: [
        {
          key: '/inquiry/list',
          label: '문의 목록',
          icon: null,
          onClick: () => navigate(`/inquiry/list`),
        },
      ],
      type: null,
    },
    // {
    //   key: 'message-management',
    //   icon: <MessageOutlined />,
    //   label: '메시지 관리',
    //   children: [
    //     {
    //       key: 'email-send-list',
    //       label: '이메일 전송내역',
    //       icon: null,
    //       onClick: () => navigate(`/message/list/email`),
    //     },
    //     {
    //       key: 'sms-send-list',
    //       label: '문자 전송내역',
    //       icon: null,
    //       onClick: () => navigate(`/message/list/sms`),
    //     },
    //     {
    //       key: 'notification-send-list',
    //       label: '푸시알림 전송내역',
    //       icon: null,
    //       onClick: () => navigate(`/message/list/notification`),
    //     },
    //     {
    //       key: 'message-template',
    //       label: '템플릿 관리',
    //       icon: null,
    //       onClick: () => navigate(`/message/template`),
    //     },
    //   ],
    //   type: null,
    // },

    {
      key: '/information',
      icon: <HddOutlined />,
      label: '나의 업체 관리',
      // children: [],
      onClick: () => navigate(`/information`),
    },
  ];

  /* ===== FUNCTION ===== */
  /**
   * 로그인 페이지로 돌아가기
   * --
   */
  const handleGoBack = () => {
    deleteCookie('JM_COFFEE_ACCESS_TOKEN', {});
    userAction.resetUser();

    const protocol = window.location.protocol;
    const port = DEPLOY_TYPE === 'local' ? ':3000' : '';

    const domain =
      DEPLOY_TYPE === 'development'
        ? `mall-jm-coffee.${DOMAIN_URL}`
        : `mall.${DOMAIN_URL}`;

    return (window.location.href = `${protocol}//${domain}${port}/#/admin`);
  };

  /**
   * 라우트 권한 설정 + 상단 헤더 Breadcrumb 설정
   * --
   * @param {Object} props
   * @returns
   */
  const PrivateCheck = ({ auth = 'SHOP_ADMIN', path, children }) => {
    const { application_status } = userStack;

    return (
      <PrivateRoute auth={auth} path={path}>
        {application_status === 'APPROVE' ? (
          children
        ) : (
          <ApplicationStatus
            title="입점심사중입니다."
            subTitle="입점심사는 최대 7일이 소요될 수 있습니다."
            content={
              <div style={{ marginTop: 30 }}>
                <Button onClick={() => handleGoBack()}>돌아가기</Button>
              </div>
            }
          />
        )}
      </PrivateRoute>
    );
  };

  /**
   * 인증 토큰 검사
   * --
   */
  const handleTokenCheck = async () => {
    const { status, data } = await authCheck();

    if (status !== 200) {
      userAction.resetUser();
      deleteCookie('JM_COFFEE_ACCESS_TOKEN', {});
      return;
    }

    userAction.setUser(data);
    shopInformationRefetch();
  };

  /* ===== HOOKS ===== */
  /**
   * 토큰 / 권한 체크
   * --
   */
  useEffect(() => {
    const token = getCookie('JM_COFFEE_ACCESS_TOKEN');
    // NOTE: 토큰 없을 경우 처리
    if (!token) {
      userAction.resetUser();

      window.alert('세션이 만료되었습니다. 로그인 후 이용해주세요!');

      const protocol = window.location.protocol;
      const port = DEPLOY_TYPE === 'local' ? ':3000' : '';
      // const domain =
      //   DEPLOY_TYPE === 'development' ? `jm-coffee.${DOMAIN_URL}` : DOMAIN_URL;
      const domain =
        DEPLOY_TYPE === 'development'
          ? `mall-jm-coffee.${DOMAIN_URL}`
          : `mall.${DOMAIN_URL}`;
      return (window.location.href = `${protocol}//${domain}${port}/#/admin`);
    }

    const userInfo = userAction.getUser();
    // NOTE: 스토어에 저장된 값이 없을 경우 처리
    if (!userInfo) handleTokenCheck();

    // eslint-disable-next-line
  }, [navigate]);

  /**
   * 쇼핑몰 정보 스토어에 업데이트
   * --
   */
  useEffect(() => {
    const call = () => {
      const { data } = shopInformation;

      const { created_at, updated_at, type, ...d } = data;

      userAction.setUser({
        ...userStack,
        ...d,
      });
    };

    if (shopInformation && shopInformation.status === 200) {
      call();
    }
    // eslint-disable-next-line
  }, [shopInformation]);

  /**
   * 메뉴선택 및 새로고침 시 선택된 메뉴 유지 로직
   * --
   */
  useEffect(() => {
    const fid = menu?.findIndex(
      ({ children }) => children?.findIndex(({ key }) => key === pathname) >= 0
    );
    if (fid >= 0) {
      setParentPath(menu[fid].key);
      setCurrentPath(pathname);
    }

    setCurrentPath(pathname);
    // alert(pathname);
  }, [pathname]);

  /* ===== RENDER ===== */
  return (
    <AdminLayout
      menu={menu}
      headerExceptPath={['certification', 'apply']}
      footerExceptPath={[]}
      siderExceptPath={['certification', 'apply']}
      selectedKeys={selectedKeys}
      onSelect={setSelectedKeys}
      brand={userStack?.shop_information_name}
      defaultSelectedKeys={[currentPath]}
      defaultOpenKeys={[parentPath]}
    >
      <Routes>
        {/* Auth Check */}
        <Route path="certification" element={<Auth />} />

        {/* 쇼핑몰 정보 입력 */}
        <Route path="apply" element={<Apply />} />

        {/* <Route
          path="main"
          element={
            <PrivateCheck>
              <AdminHome />
            </PrivateCheck>
          }
        /> */}
        <Route
          path="main"
          element={
            <PrivateCheck>
              <Main />
            </PrivateCheck>
          }
        />

        {/* 회원관리 */}
        <Route path="users">
          <Route
            index
            path="dashboard"
            element={
              <PrivateCheck
                path={[{ title: '회원 관리' }, { title: '대시보드' }]}
              >
                <UserDashboard />
              </PrivateCheck>
            }
          />
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '회원 관리' }, { title: '회원 목록' }]}
                >
                  <UserList />
                </PrivateCheck>
              }
            />
            <Route path=":userId" element={<UserDetail />} />
          </Route>
          <Route
            path="period"
            element={
              <PrivateCheck
                path={[{ title: '회원 관리' }, { title: '기간별 회원 목록' }]}
              >
                <UserPeriodList />
              </PrivateCheck>
            }
          />
          <Route
            path="register"
            element={
              <PrivateCheck
                path={[{ title: '회원 관리' }, { title: '회원 등록/수정' }]}
              >
                <UserRegister />
              </PrivateCheck>
            }
          />
          <Route path="rating">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '회원 관리' }, { title: '회원등급 관리' }]}
                >
                  <RatingList />
                </PrivateCheck>
              }
            />
            <Route path=":ratingId" element={<RatingDetail />} />
            <Route
              path="register"
              element={
                <PrivateCheck
                  path={[
                    { title: '회원 관리' },
                    { title: '회원등급 관리' },
                    { title: '회원등급 생성' },
                  ]}
                >
                  <RatingRegister />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 회원관리 끝 */}

        {/* 상품/재고 */}
        <Route path="product">
          <Route path="dashboard">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '상품/재고 관리' },
                    { title: '상품 대시보드' },
                  ]}
                >
                  <ProductDashboard />
                </PrivateCheck>
              }
            />
          </Route>
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '상품/재고 관리' }, { title: '상품 목록' }]}
                >
                  <ProductList />
                </PrivateCheck>
              }
            />
            <Route path=":productId" element={<ProductDetail />} />
          </Route>
          <Route
            path="register"
            element={
              <PrivateCheck
                path={[{ title: '상품/재고 관리' }, { title: '상품 등록' }]}
              >
                <ProductRegister />
              </PrivateCheck>
            }
          />
        </Route>
        <Route path="product-order">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '상품/재고 관리' }, { title: '발주 등록' }]}
                >
                  <ProductOrderList />
                </PrivateCheck>
              }
            />
            <Route path=":orderId" element={<div>발주 상세</div>} />
          </Route>
        </Route>
        <Route path="product-audit">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '상품/재고 관리' },
                    { title: '상품심사 목록' },
                  ]}
                >
                  <ProductAuditList />
                </PrivateCheck>
              }
            />
            <Route path=":auditId" element={<div>상품심사 상세</div>} />
          </Route>
        </Route>
        {/* 상품/재고 끝 */}

        {/* 프로모션관리 */}
        <Route path="promotion">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '프로모션 관리' },
                    { title: '프로모션 목록' },
                  ]}
                >
                  <PromotionList />
                </PrivateCheck>
              }
            />
            <Route path=":promotionId" element={<PromotionDetail />} />
            <Route
              path="register"
              element={
                <PrivateCheck
                  path={[
                    { title: '프로모션 관리' },
                    { title: '프로모션 생성/수정' },
                  ]}
                >
                  <PromotionRegister />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="proposal">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '프로모션 관리' },
                    { title: '프로모션 제안 목록' },
                  ]}
                >
                  <ProposalList />
                </PrivateCheck>
              }
            />
            <Route
              path=":proposalId"
              element={
                <PrivateCheck
                  path={[
                    { title: '프로모션 관리' },
                    { title: '프로모션 제안 상세정보' },
                  ]}
                >
                  <ProposalDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 프로모션관리 끝 */}

        {/* 정산관리 */}
        <Route path="calculate">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '정산 관리' }, { title: '정산내역 조회' }]}
                >
                  <CalculateList />
                </PrivateCheck>
              }
            />
            <Route path=":calculateId" element={<CalculateDetail />} />
          </Route>
          <Route
            path="application"
            element={
              <PrivateCheck
                path={[{ title: '정산 관리' }, { title: '출금신청 내역' }]}
              >
                <CalculateApplication />
              </PrivateCheck>
            }
          />
          <Route
            path="charge"
            element={
              <PrivateCheck
                path={[{ title: '정산 관리' }, { title: '수수료 관리' }]}
              >
                <ChargeList />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 정산관리 끝 */}

        {/* 사이트관리 */}
        <Route path="notice">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '사이트 관리' }, { title: '공지사항 관리' }]}
                >
                  <NoticeList />
                </PrivateCheck>
              }
            />
            <Route path=":noticeId" element={<NoticeDetail />} />
          </Route>
        </Route>
        <Route path="popup">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '사이트 관리' }, { title: '팝업 관리' }]}
                >
                  <PopupList />
                </PrivateCheck>
              }
            />
            <Route path=":popupId" element={<PopupDetail />} />
          </Route>
        </Route>
        <Route path="banner">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '사이트 관리' }, { title: '배너 관리' }]}
                >
                  <BannerList />
                </PrivateCheck>
              }
            />
            <Route path=":bannerId" element={<BannerDetail />} />
          </Route>
        </Route>
        <Route path="policy">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '사이트 관리' }, { title: '정책 관리' }]}
                >
                  <PolicyList />
                </PrivateCheck>
              }
            />
            <Route path=":policyId" element={<PolicyDetail />} />
          </Route>
        </Route>
        <Route path="seo">
          <Route index path="main" element={<SEOMain />} />
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '사이트 관리' },
                    { title: '검색엔진 최적화' },
                  ]}
                >
                  <SEOList />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 사이트관리 끝 */}

        {/* 리뷰 */}
        <Route path="review">
          <Route
            index
            path="dashboard"
            element={
              <PrivateCheck
                path={[{ title: '리뷰 관리' }, { title: '리뷰 대시보드' }]}
              >
                <ReviewDashboard />
              </PrivateCheck>
            }
          />
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '리뷰 관리' }, { title: '리뷰 목록' }]}
                >
                  <ReviewList />
                </PrivateCheck>
              }
            />
            <Route
              path=":reviewId"
              element={
                <PrivateCheck
                  path={[{ title: '리뷰 관리' }, { title: '리뷰 상세정보' }]}
                >
                  <ReviewDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 리뷰 끝 */}

        {/* 입점업체 관리 */}
        <Route path="brand">
          <Route
            path="dashboard"
            element={
              <PrivateCheck
                path={[{ title: '입점업체 관리' }, { title: '대시보드' }]}
              >
                <BrandDashboard />
              </PrivateCheck>
            }
          />
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '입점업체 관리' }, { title: '입점 목록' }]}
                >
                  <BrandList />
                </PrivateCheck>
              }
            />
            <Route
              path=":brandId"
              element={
                <PrivateCheck
                  path={[
                    { title: '입점업체 관리' },
                    { title: '입점업체 상세정보' },
                  ]}
                >
                  <BrandDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="brand-audit">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '입점업체 관리' },
                    { title: '입점심사 목록' },
                  ]}
                >
                  <BrandAuditList />
                </PrivateCheck>
              }
            />
            <Route
              path=":brandAuditId"
              element={
                <PrivateCheck
                  path={[
                    { title: '입점업체 관리' },
                    { title: '입점심사 상세정보' },
                  ]}
                >
                  <BrandAuditDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 입점업체 관리 끝 */}

        {/* 매장 관리 */}
        <Route path="store">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '매장 관리' }, { title: '매장 관리' }]}
                >
                  <StoreList />
                </PrivateCheck>
              }
            />
            <Route
              path=":storeId"
              element={
                <PrivateCheck
                  path={[{ title: '매장 관리' }, { title: '매장 상세정보' }]}
                >
                  <StoreDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 매장 관리 끝 */}

        {/* 카테고리 */}
        <Route path="categories">
          <Route
            path="list"
            element={
              <PrivateCheck
                path={[{ title: '카테고리 관리' }, { title: '카테고리 목록' }]}
              >
                <CategoryList />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 카테고리 끝 */}

        {/* 선물 관리 */}
        <Route path="gift">
          <Route
            index
            path="dashboard"
            element={
              <PrivateCheck
                path={[{ title: '선물 관리' }, { title: '대시보드' }]}
              >
                <GiftDashboard />
              </PrivateCheck>
            }
          />
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '선물 관리' }, { title: '선물 내역 목록' }]}
                >
                  <GiftList />
                </PrivateCheck>
              }
            />
            <Route
              path=":giftId"
              element={
                <PrivateCheck
                  path={[
                    { title: '선물 관리' },
                    { title: '선물 내역 상세정보' },
                  ]}
                >
                  <GiftDetail />
                </PrivateCheck>
              }
            />
          </Route>
          <Route path="products">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '선물 관리' }, { title: '선물 상품 목록' }]}
                >
                  <GiftProductList />
                </PrivateCheck>
              }
            />
            <Route
              path=":giftProductId"
              element={
                <PrivateCheck
                  path={[
                    { title: '선물 관리' },
                    { title: '선물 상품 상세정보' },
                  ]}
                >
                  <GiftProductDetail />
                </PrivateCheck>
              }
            />
          </Route>
          <Route
            index
            path="categories"
            element={
              <PrivateCheck
                path={[{ title: '선물 관리' }, { title: '선물 카테고리 관리' }]}
              >
                <GiftCategoryList />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 선물 관리 끝 */}

        {/* 메뉴 */}
        <Route path="menu">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '메뉴 관리' }, { title: '메뉴 목록' }]}
                >
                  <MenuList />
                </PrivateCheck>
              }
            />
            <Route
              path=":menuId"
              element={
                <PrivateCheck
                  path={[{ title: '메뉴 관리' }, { title: '메뉴 상세정보' }]}
                >
                  <MenuDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 메뉴 끝 */}

        {/* 태그 */}
        <Route path="tag">
          <Route
            path="list"
            element={
              <PrivateCheck
                path={[{ title: '태그 관리' }, { title: '태그 목록' }]}
              >
                <TagList />
              </PrivateCheck>
            }
          />
          <Route
            path="detail"
            element={
              <PrivateCheck
                path={[{ title: '태그 관리' }, { title: '태그 상세정보' }]}
              >
                <TagDetail />
              </PrivateCheck>
            }
          />
          <Route
            path="register"
            element={
              <PrivateCheck
                path={[{ title: '태그 관리' }, { title: '태그 등록/수정' }]}
              >
                <TagRegister />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 태그 끝 */}

        {/* 추천검색어 */}
        <Route path="searchword">
          <Route
            path="list"
            element={
              <PrivateCheck
                path={[
                  { title: '추천검색어 관리' },
                  { title: '추천검색어 목록' },
                ]}
              >
                <SearchWordList />
              </PrivateCheck>
            }
          />
          <Route
            path="detail"
            element={
              <PrivateCheck
                path={[
                  { title: '추천검색어 관리' },
                  { title: '추천검색어 상세정보' },
                ]}
              >
                <SearchWordDetail />
              </PrivateCheck>
            }
          />
          <Route
            path="register"
            element={
              <PrivateCheck
                path={[
                  { title: '추천검색어 관리' },
                  { title: '추천검색어 등록/수정' },
                ]}
              >
                <SearchWordRegister />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 추천 검색어 끝 */}

        {/* 주문관리 */}
        <Route path="order">
          <Route path="dashboard">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '주문 관리' },
                    { title: '주문관리 대시보드' },
                  ]}
                >
                  <OrderDashboard />
                </PrivateCheck>
              }
            />
          </Route>
          <Route path="list">
            <Route
              // path="list"
              index
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '전체주문조회' }]}
                >
                  <OrderList />
                </PrivateCheck>
              }
            />
            <Route
              path=":order_type"
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '입금전 관리' }]}
                >
                  <OrderList />
                </PrivateCheck>
              }
            />
            {/* <Route
              path=":orderId"
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '주문 상세정보' }]}
                ></PrivateCheck>
              }
            /> */}
          </Route>
        </Route>
        <Route path="shipping">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '배송 목록' }]}
                >
                  <ShippingList />
                </PrivateCheck>
              }
            />
            <Route
              path=":shipping_type"
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '배송 상태 관리' }]}
                >
                  <ShippingList />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="regular">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '정기배송 목록' }]}
                >
                  <RegularList />
                </PrivateCheck>
              }
            />
            <Route
              path=":orderId"
              element={
                <PrivateCheck
                  path={[
                    { title: '주문 관리' },
                    { title: '정기배송 상세정보' },
                  ]}
                ></PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="rent">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '렌탈 목록' }]}
                >
                  <RentList />
                </PrivateCheck>
              }
            />
            <Route
              path=":orderId"
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '렌탈 상세정보' }]}
                ></PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="payment">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '결제 목록' }]}
                >
                  <PaymentList />
                </PrivateCheck>
              }
            />
            <Route
              path=":orderId"
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '결제 상세정보' }]}
                ></PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="refund">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '주문 관리' },
                    { title: '교환/반품/환불 처리완료' },
                  ]}
                >
                  <RefundList />
                </PrivateCheck>
              }
            />
            <Route
              path=":orderId"
              element={
                <PrivateCheck
                  path={[{ title: '주문 관리' }, { title: '환불 상세정보' }]}
                ></PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 주문관리 끝 */}

        {/* 관리자 관리 */}
        <Route path="manager">
          <Route
            path="list"
            index
            element={
              <PrivateCheck
                path={[{ title: '관리자 관리' }, { title: '관리자 목록' }]}
              >
                <ManagerList defaultShop={userStack?.shop_information_id} />
              </PrivateCheck>
            }
          />
          <Route
            path=":adminId"
            element={
              <PrivateCheck
                path={[{ title: '관리자 관리' }, { title: '관리자 등록' }]}
              >
                <ManagerRegister defaultShop={userStack?.shop_information_id} />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 관리자 관리 끝 */}

        {/* 매출 관리 */}
        <Route path="sales">
          <Route path="dashboard">
            <Route
              index
              path=":type"
              element={
                <PrivateCheck
                  path={[{ title: '매출 관리' }, { title: '대시보드' }]}
                >
                  <SaleDashboard />
                </PrivateCheck>
              }
            />
            {/* 분류별 통계 [daily, menu, payment] */}
            <Route
              path=":type"
              element={
                <PrivateCheck
                  path={[{ title: '매출 관리' }, { title: '일자별' }]}
                >
                  <SaleDashboard />
                </PrivateCheck>
              }
            />
          </Route>

          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '매출 관리' }, { title: '매출 목록' }]}
                >
                  <SaleList />
                </PrivateCheck>
              }
            />
            <Route
              path=":salesId"
              element={
                <PrivateCheck
                  path={[{ title: '매출 관리' }, { title: '매출 상세정보' }]}
                >
                  <SaleDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        <Route path="store-sales">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '매출 관리' }, { title: '매출 목록' }]}
                >
                  <SaleList />
                </PrivateCheck>
              }
            />
            <Route
              path=":salesId"
              element={
                <PrivateCheck
                  path={[{ title: '매출 관리' }, { title: '매출 상세정보' }]}
                >
                  <SaleDetail />
                </PrivateCheck>
              }
            />
          </Route>
        </Route>
        {/* 매출 관리 끝 */}

        {/* 장바구니 관리 */}
        <Route path="cart">
          <Route
            path="analyze"
            element={
              <PrivateCheck
                path={[{ title: '장바구니 관리' }, { title: '장바구니 분석' }]}
              >
                <CartAnalyze />
              </PrivateCheck>
            }
          />
          <Route
            path="settings"
            element={
              <PrivateCheck
                path={[{ title: '장바구니 관리' }, { title: '장바구니 설정' }]}
              >
                <CartSettings />
              </PrivateCheck>
            }
          />
          <Route
            path="list"
            index
            element={
              <PrivateCheck
                path={[{ title: '장바구니 관리' }, { title: '장바구니 분석' }]}
              >
                <CartList />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 장바구니 관리 끝 */}

        {/* 통계 관리 */}
        <Route path="statistics">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '통계 관리' }, { title: '대시보드' }]}
                >
                  <StatisticList />
                </PrivateCheck>
              }
            />
            <Route
              path=":statisticsId"
              element={
                <PrivateCheck
                  path={[{ title: '통계 관리' }, { title: '통계 상세정보' }]}
                >
                  <StatisticDetail />
                </PrivateCheck>
              }
            />
          </Route>
          <Route
            path="dashboard"
            element={
              <PrivateCheck
                path={[{ title: '통계 관리' }, { title: '대시보드' }]}
              >
                <StatisticDashboard />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 통계 관리 끝 */}

        {/* 문의 관리 */}
        <Route path="inquiry">
          <Route
            path="list"
            index
            element={
              <PrivateCheck
                path={[{ title: '문의 관리' }, { title: '문의 목록' }]}
              >
                <InquiryList />
              </PrivateCheck>
            }
          ></Route>
        </Route>
        {/* 문의 관리 끝 */}

        {/* 메시지 관리 */}
        <Route path="message">
          <Route path="list">
            <Route
              path="email"
              element={
                <PrivateCheck
                  path={[
                    { title: '메시지 관리' },
                    { title: '이메일 전송내역' },
                  ]}
                >
                  <EmailList />
                </PrivateCheck>
              }
            />
            <Route
              path="sms"
              index
              element={
                <PrivateCheck
                  path={[{ title: '메시지 관리' }, { title: '문자 전송내역' }]}
                >
                  <SMSList />
                </PrivateCheck>
              }
            />
            <Route
              path="notification"
              index
              element={
                <PrivateCheck
                  path={[
                    { title: '메시지 관리' },
                    { title: '푸시알림 전송내역' },
                  ]}
                >
                  <NotificationList />
                </PrivateCheck>
              }
            />
          </Route>
          <Route
            path="template"
            element={
              <PrivateCheck
                path={[{ title: '메시지 관리' }, { title: '템플릿 관리' }]}
              >
                <MessageTemplate />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 메시지 관리 끝 */}

        {/* 나의 업체 관리 */}
        <Route
          index
          path="information"
          element={
            <PrivateCheck path={[{ title: '나의 업체 관리' }]}>
              <ShopInformation />
            </PrivateCheck>
          }
        />
        {/* 나의 업체 관리 끝 */}

        {/* 출고지 관리 */}
        <Route path="forwarding">
          <Route
            path="location"
            element={
              <PrivateCheck
                path={[{ title: '상품/재고 관리' }, { title: '출고지 관리' }]}
              >
                <ForwardingLocationContainer
                  defaultShopId={userStack?.shop_information_id}
                  permission={'SHOP_ADMIN'}
                />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 출고지 관리 끝 */}
      </Routes>
    </AdminLayout>
  );
};

export default AdminRoutes;
